import { useState, useEffect } from "react";
import { ChevronRight, Users } from "lucide-react";
import { buildTree } from "./layouts";
import { EmployeeCard } from "./EmployeeNode";

export default function TeamGrid({ employees, view, onSelect, focusNode }) {
  const { roots, childrenOf, byEid } = buildTree(employees, view?.root_employee_id);
  const [currentEid, setCurrentEid] = useState(null);

  useEffect(() => {
    if (focusNode) {
      const emp = employees.find((e) => e.id === focusNode.id);
      if (emp) setCurrentEid(emp.manager_employee_id && byEid[emp.manager_employee_id] ? emp.manager_employee_id : emp.employee_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNode]);

  const current = currentEid ? byEid[currentEid] : roots[0];
  if (!current) return <div className="p-10 text-center text-[#676a82]">No employees to display.</div>;

  const crumbs = [];
  let walk = current;
  while (walk) {
    crumbs.unshift(walk);
    walk = walk.manager_employee_id ? byEid[walk.manager_employee_id] : null;
  }
  const team = childrenOf[current.employee_id] || [];

  return (
    <div className="h-full overflow-auto p-8" data-testid="team-grid-view">
      <div className="max-w-6xl mx-auto">
        <nav className="flex items-center gap-1 flex-wrap text-sm font-semibold text-[#676a82] mb-6">
          {crumbs.map((c, i) => (
            <span key={c.id} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={14} />}
              <button data-testid={`breadcrumb-${c.employee_id}`} onClick={() => setCurrentEid(c.employee_id)}
                className={`px-2 py-0.5 rounded-lg hover:bg-[#F0F3FF] ${i === crumbs.length - 1 ? "text-[#6161FF] font-bold" : ""}`}>
                {c.full_name}
              </button>
            </span>
          ))}
        </nav>
        <div className="flex justify-center mb-8">
          <EmployeeCard emp={current} config={view} childCount={team.length} onClick={() => onSelect(current)} />
        </div>
        {team.length > 0 ? (
          <>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#676a82] mb-4">
              <Users size={13} /> Direct reports ({team.length})
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {team.map((m) => {
                const sub = (childrenOf[m.employee_id] || []).length;
                return (
                  <div key={m.id} className="relative">
                    <EmployeeCard emp={m} config={view} childCount={sub} onClick={() => onSelect(m)} />
                    {sub > 0 && (
                      <button data-testid={`drill-down-${m.employee_id}`} onClick={() => setCurrentEid(m.employee_id)}
                        className="mt-1.5 w-full text-xs font-bold text-[#6161FF] hover:underline flex items-center justify-center gap-1">
                        View team <ChevronRight size={12} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <p className="text-center text-[#676a82] font-medium">No direct reports.</p>
        )}
      </div>
    </div>
  );
}
